<template>
  <div class="oauth-callback-page">
    <b-row class="justify-content-center">
      <b-col md="8" lg="6">
        <b-card class="oauth-callback-page__card text-center">
          <div v-if="isProcessing" class="oauth-callback-page__processing">
            <b-spinner
              variant="primary"
              class="mb-3"
              style="width: 3rem; height: 3rem;"
            ></b-spinner>
            <h5 class="mb-2">Đang xử lý kết nối...</h5>
            <p class="text-muted">
              Vui lòng đợi trong giây lát, chúng tôi đang hoàn tất kết nối với
              {{ platformName }}
            </p>
          </div>

          <div v-else-if="error" class="oauth-callback-page__error">
            <div class="oauth-callback-page__error-icon mb-3">
              <i
                class="flaticon2-delete text-danger"
                style="font-size: 3rem;"
              ></i>
            </div>
            <h5 class="text-danger mb-3">Kết nối thất bại</h5>
            <div class="alert alert-danger text-left">
              <p class="mb-0">{{ error }}</p>
            </div>
            <div class="mt-4">
              <b-button variant="primary" @click="retryConnection" class="mr-2">
                <i class="flaticon2-refresh"></i> Thử lại
              </b-button>
              <b-button variant="secondary" @click="backToList">
                Quay lại danh sách
              </b-button>
            </div>
          </div>

          <div v-else-if="success" class="oauth-callback-page__success">
            <div class="oauth-callback-page__success-icon mb-3">
              <i
                class="flaticon2-check-mark text-success"
                style="font-size: 3rem;"
              ></i>
            </div>
            <h5 class="text-success mb-2">Đang chuyển hướng...</h5>
            <p class="text-muted">
              Kết nối thành công, đang chuyển đến bước tiếp theo
            </p>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { getToastInstance } from '@/utils/toastHelper';
import { RepositoryFactory } from '@/repositories/RepositoryFactory';

const platformRepository = RepositoryFactory.get('platformIntegration');

export default {
  name: 'OAuthCallbackPage',
  data() {
    return {
      isProcessing: true,
      error: null,
      success: false,
      platform: null,
      code: null,
      state: null,
      shopId: null,
    };
  },
  computed: {
    platformName() {
      const names = {
        shopee: 'Shopee',
        tiktok: 'TikTok Shop',
      };
      return names[this.platform] || this.platform || 'sàn thương mại điện tử';
    },
  },
  async created() {
    this.extractParams();
    await this.processCallback();
  },
  methods: {
    ...getToastInstance(),

    extractParams() {
      const query = this.$route.query;
      this.platform = this.$route.params.platform;
      this.code = query.code;
      this.state = query.state;
      this.shopId = query.shop_id;

      const errorParam = query.error;
      const errorDescription = query.error_description;

      if (errorParam) {
        this.isProcessing = false;
        if (errorParam === 'access_denied') {
          this.error =
            'Bạn đã từ chối quyền truy cập. Vui lòng chấp nhận để tiếp tục kết nối.';
        } else {
          this.error =
            errorDescription || `Lỗi từ ${this.platformName}: ${errorParam}`;
        }
      }

      if (!this.code || !this.shopId) {
        this.isProcessing = false;
        this.error = 'Thiếu thông tin xác thực. Vui lòng thử lại.';
      }

      if (this.platform === 'tiktok' && !this.state) {
        this.isProcessing = false;
        this.error = 'Thiếu thông tin xác thực (state). Vui lòng thử lại.';
      }
    },

    async processCallback() {
      if (this.error) {
        return;
      }

      try {
        this.isProcessing = true;
        this.error = null;

        const response = await platformRepository.handleCallback(
          this.platform,
          this.code,
          this.state,
          this.shopId,
        );

        this.success = true;

        if (response.needsShopSelection) {
          this.$router.push({
            name: 'platform-integration-shop-selection',
            query: {
              platform: this.platform,
              configId: response.configId,
            },
          });
        } else {
          this.$router.push({
            name: 'platform-integration-success',
            query: {
              configId: response.configId,
              shopId: response.shopId,
              shopName: response.shopName,
              platform: this.platform,
            },
          });
        }
      } catch (error) {
        console.error('Error processing OAuth callback:', error);
        this.isProcessing = false;
        this.success = false;

        if (error.response?.data?.message) {
          this.error = error.response.data.message;
        } else if (error.response?.status === 400) {
          this.error =
            'Yêu cầu không hợp lệ hoặc đã hết hạn. Vui lòng thử kết nối lại.';
        } else if (error.response?.status === 401) {
          this.error = 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.';
        } else if (error.response?.status === 409) {
          this.error = 'Gian hàng này đã được kết nối trước đó.';
        } else if (error.response?.status === 503) {
          this.error = `Không thể kết nối với ${this.platformName} lúc này. Vui lòng thử lại sau.`;
        } else {
          this.error = `Không thể kết nối với ${this.platformName}. Vui lòng thử lại.`;
        }

        this.makeToastFailure(this.error);
      }
    },

    retryConnection() {
      this.$router.push({ name: 'platform-integration-connect' });
    },

    backToList() {
      this.$router.push({ name: 'platform-integration-list' });
    },
  },
};
</script>

<style lang="scss" scoped>
.oauth-callback-page {
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;

  &__card {
    padding: 2rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border: 1px solid #e4e6ef;
  }

  &__processing,
  &__error,
  &__success {
    padding: 1rem;
  }

  &__error-icon,
  &__success-icon {
    i {
      display: inline-block;
      width: 80px;
      height: 80px;
      line-height: 80px;
      border-radius: 50%;
      background: #f3f6f9;
    }
  }

  &__error-icon i {
    background: #ffe2e5;
  }

  &__success-icon i {
    background: #c9f7f5;
  }

  .alert {
    border-radius: 8px;
  }
}
</style>
